import React from 'react';

const allChoicePatterns = /\{\s*[0-9]+\s*,\s*choice\s*,[^}]+\}/g;
const nextChoicePattern = /\{\s*([0-9]+)\s*,\s*choice\s*,([^}]+)\}/;

class Choice {
    relation: string;
    value: number;
    expr: string;

    constructor(value: string, relation: string, expr: string) {
        this.relation = relation;
        this.value = parseFloat(value);
        this.expr = expr;
    }

    test(num: number): boolean {
        let matches = false;
        switch (this.relation) {
            case '#':
                matches = num === this.value;
                break;
            case '<':
                matches = num > this.value;
                break;
            default:
                throw new Error(`invalid case in choice pattern: ${this.relation}`);
        }
        return matches;
    }

    format(): string {
        return this.expr;
    }
}

class ChoicePattern {
    static cache: Record<string, Choice[]> = {};
    pattern: string;
    choices: Choice[];

    constructor(pattern: string) {
        this.pattern = pattern;
        this.choices = [];
        this.parse();
    }

    parse(): void {
        const pattern = /\s*(\d+\.?\d*)\s*([#<])(.*)/;
        const caseStrings = this.pattern.split(/[|]/);
        if (!(this.choices = ChoicePattern.cache[this.pattern] || []).length) {
            this.choices = caseStrings.map((str) => {
                const match = str.match(pattern);
                if (!match || match.length !== 4) {
                    throw new Error('invalid case in choice pattern');
                }
                return new Choice(match[1], match[2], match[3]);
            });
            ChoicePattern.cache[this.pattern] = this.choices = this.choices.reverse();
        }
    }

    test(choiceNumber: number): string {
        const matchCase = this.choices.filter((choice) => choice.test(choiceNumber) || choice.value < choiceNumber).shift();
        // reason: choices are always built from a valid pattern — no matching case is a programming error
        return matchCase!.format();
    }
}

const i18nChoicePattern = (origVal: string, argsData: Array<string | number | React.ReactNode>): string => {
    let choicePatternsLen: number;
    let choicePatternMatch: RegExpMatchArray | null;
    let paramIndex: number;
    let choiceParamNumberValue: number;
    let val = origVal;

    const choicePatterns = val.match(allChoicePatterns);
    if (choicePatterns !== null) {
        const args = argsData.map((e) =>
            typeof e === 'object' && e !== null ? ((e as React.ReactElement).props as { children: React.ReactNode }).children : e
        );
        choicePatternsLen = choicePatterns.length;
        for (let i = 0; i < choicePatternsLen; i++) {
            choicePatternMatch = choicePatterns[i].match(nextChoicePattern);
            if (choicePatternMatch !== null) {
                paramIndex = parseInt(choicePatternMatch[1], 10);
                choiceParamNumberValue = parseFloat(String(args[paramIndex] as unknown));

                // if there is a parameter value for the placeholder the choice pattern is controlled by
                if (!isNaN(choiceParamNumberValue)) {
                    // then parse the ChoicePattern
                    const choice = new ChoicePattern(choicePatternMatch[2]);
                    val = val.replace(choicePatterns[i], choice.test(choiceParamNumberValue));
                }
            }
        }
    }

    return val;
};

export default i18nChoicePattern;
